import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { from, issuerLabel, postgrestSource, unwrap, useHasRole, useIssuers, useOrg } from "@vixio/data";
import {
  Button, DataTable, KpiRow, KpiTile, PageHeader, StatusFromMap, StatusPill, addDaysIso, formatDate, formatMoney, monthStartIso, sumCents, todayIso,
} from "@vixio/ui";
import { TITLE_STATUS, type InstallmentView } from "./api";
import { NewReceivableDialog, SettleDialog } from "./dialogs";

export function useReceivableKpis(orgId: string) {
  const today = todayIso();
  return useQuery({
    queryKey: ["finance", "kpis", orgId, today],
    queryFn: async () => {
      const open = await unwrap<Array<{ open_cents: number; due_date: string }>>(
        from("finance", "v_installments").select("open_cents, due_date").eq("org_id", orgId).in("status", ["open", "partially_paid"]).limit(5000),
      );
      const receipts = await unwrap<Array<{ total_cents: number }>>(
        from("finance", "receipts").select("total_cents").eq("org_id", orgId).gte("paid_on", monthStartIso(today)).lte("paid_on", today).limit(5000),
      );
      const reversals = await unwrap<Array<{ total_cents: number }>>(
        from("finance", "receipt_reversals").select("total_cents").eq("org_id", orgId).gte("reversed_on", monthStartIso(today)).limit(5000),
      );
      return {
        open: sumCents(open.map((r) => r.open_cents)),
        overdue: sumCents(open.filter((r) => r.due_date < today).map((r) => r.open_cents)),
        overdueCount: open.filter((r) => r.due_date < today).length,
        next30: sumCents(open.filter((r) => r.due_date >= today && r.due_date <= addDaysIso(today, 30)).map((r) => r.open_cents)),
        receivedMonth: sumCents(receipts.map((r) => r.total_cents)) - sumCents(reversals.map((r) => r.total_cents)),
      };
    },
  });
}

export function ReceivablesPage() {
  const org = useOrg();
  const navigate = useNavigate();
  const canWrite = useHasRole("manager");
  const issuers = useIssuers(org.id);
  const kpis = useReceivableKpis(org.id);
  const [creating, setCreating] = useState(false);
  const [settling, setSettling] = useState<InstallmentView | null>(null);
  const today = todayIso();
  const issuerName = new Map((issuers.data ?? []).map((i) => [i.id, issuerLabel(i)]));

  const source = useMemo(
    () =>
      postgrestSource<InstallmentView>({
        schema: "finance",
        table: "v_installments",
        orgId: org.id,
        searchColumns: ["counterparty_name", "description", "counterparty_doc"],
        filters: {
          status: (qb, v) =>
            v === "open_all" ? qb.in("status", ["open", "partially_paid"]) : v === "overdue" ? qb.in("status", ["open", "partially_paid"]).lt("due_date", today) : qb.eq("status", v),
        },
        tiebreak: "installment_id",
        keyExtra: [today],
      }),
    [org.id, today],
  );

  return (
    <div className="vx-content">
      <PageHeader
        title="Contas a receber"
        description="Parcelas por vencimento. Recebimentos são imutáveis: correção é estorno, com 2FA."
        actions={
          canWrite ? (
            <Button variant="primary" icon={<Plus />} onClick={() => setCreating(true)}>
              Nova conta a receber
            </Button>
          ) : null
        }
      />
      <KpiRow>
        <KpiTile label="Em aberto" value={formatMoney(kpis.data?.open ?? 0)} />
        <KpiTile label="Vencido" value={formatMoney(kpis.data?.overdue ?? 0)} foot={`${kpis.data?.overdueCount ?? 0} parcela(s)`} tone={(kpis.data?.overdue ?? 0) > 0 ? "crit" : undefined} />
        <KpiTile label="Vence em 30 dias" value={formatMoney(kpis.data?.next30 ?? 0)} />
        <KpiTile label="Recebido no mês" value={formatMoney(kpis.data?.receivedMonth ?? 0)} foot="líquido de estornos" tone={(kpis.data?.receivedMonth ?? 0) > 0 ? "ok" : undefined} />
      </KpiRow>
      <div className="vx-section">
        <DataTable<InstallmentView>
          caption="Parcelas a receber"
          source={source}
          rowKey={(r) => r.installment_id}
          onRowClick={(r) => navigate(`/dinheiro/receber/${r.receivable_id}`)}
          initialSort={{ key: "due_date", dir: "asc" }}
          initialFilters={{ status: "open_all" }}
          searchPlaceholder="Buscar por pagador, descrição ou documento"
          exportName="contas-a-receber"
          filters={[
            {
              key: "status",
              label: "Situação",
              all: "Todas",
              options: [
                { value: "open_all", label: "Em aberto" },
                { value: "overdue", label: "Vencidas" },
                { value: "paid", label: "Pagas" },
                { value: "canceled", label: "Canceladas" },
                { value: "renegotiated", label: "Renegociadas" },
              ],
            },
            { key: "issuer_id", label: "CNPJ", all: "Todos os CNPJs", options: (issuers.data ?? []).map((i) => ({ value: i.id, label: issuerLabel(i) })) },
          ]}
          columns={[
            {
              key: "due_date",
              header: "Vencimento",
              sortable: true,
              kind: "date",
              cell: (r) => {
                const late = (r.status === "open" || r.status === "partially_paid") && r.due_date < today;
                return (
                  <span className={late ? "num text-crit" : "num"}>
                    {formatDate(r.due_date)}
                    {late ? <span className="vx-table__sub">{r.days_overdue} dia(s) de atraso</span> : null}
                  </span>
                );
              },
            },
            {
              key: "counterparty_name",
              header: "Pagador",
              sortable: true,
              cell: (r) => (
                <span className="vx-table__primary">
                  <Link to={`/dinheiro/receber/${r.receivable_id}`} onClick={(e) => e.stopPropagation()}>
                    {r.counterparty_name}
                  </Link>
                  <span className="vx-table__sub">{r.description}</span>
                </span>
              ),
            },
            { key: "number", header: "Parcela", cell: (r) => (r.label ? `${r.number} · ${r.label}` : String(r.number)), csv: (r) => r.number },
            { key: "issuer_id", header: "CNPJ", cell: (r) => issuerName.get(r.issuer_id ?? "") ?? <span className="muted">a definir</span>, csv: (r) => issuerName.get(r.issuer_id ?? "") ?? "" },
            { key: "amount_cents", header: "Valor", kind: "money", sortable: true },
            { key: "open_cents", header: "Em aberto", kind: "money", sortable: true },
            { key: "status", header: "Situação", cell: (r) => <StatusFromMap map={TITLE_STATUS} value={r.status} />, csv: (r) => TITLE_STATUS[r.status]?.label ?? r.status },
            {
              key: "actions",
              header: <span className="sr-only">Ações</span>,
              align: "right",
              csv: false,
              cell: (r) =>
                canWrite && (r.status === "open" || r.status === "partially_paid") ? (
                  <Button
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSettling(r);
                    }}
                  >
                    Receber
                  </Button>
                ) : r.renegotiation ? (
                  <StatusPill plain>acordo</StatusPill>
                ) : null,
            },
          ]}
        />
      </div>
      <NewReceivableDialog open={creating} onOpenChange={setCreating} onCreated={(id) => navigate(`/dinheiro/receber/${id}`)} />
      <SettleDialog inst={settling} open={settling !== null} onOpenChange={(o) => !o && setSettling(null)} />
    </div>
  );
}
